import { useEffect, useState, type ReactNode } from "react";
import { FileText, Info, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { WaveSourcePicker, type WaveSource } from "@/components/admin_dashboard/FollowUpWavesEditor";

export const MAX_INSTRUCTIONS_LENGTH = 2000;

export interface WorkflowMessageSettingsRow {
  content_mode?: string | null;
  ai_source_ids?: string[] | null;
  template_source_ids?: string[] | null;
  ai_instructions?: string | null;
  config?: Record<string, unknown> | null;
}

// Workflows that aren't wired to the backend yet keep their settings in this
// browser, per business, until a backend table exists for them.
export function useStoredSettings<T extends object>(storageKey: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? { ...initial, ...(JSON.parse(raw) as Partial<T>) } : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // Storage can be unavailable; the settings just won't survive a reload.
    }
  }, [storageKey, value]);
  return [value, (patch: Partial<T>) => setValue((current) => ({ ...current, ...patch }))] as const;
}

/**
 * Server-backed workflow settings stored in `public.workflow_message_settings`,
 * with automatic one-time migration from `window.localStorage` and fallback to
 * local state when `businessId` is absent.
 */
export function useServerWorkflowSettings<T extends object>(options: {
  businessId?: string;
  workflowId: string;
  storageKey: string;
  initial: T;
  fromServer: (row: WorkflowMessageSettingsRow, initial: T) => T;
  toServer: (value: T) => WorkflowMessageSettingsRow;
}) {
  const { businessId, workflowId, storageKey, initial, fromServer, toServer } = options;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["workflow-message-settings", businessId, workflowId];

  const [localValue, setLocalValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? { ...initial, ...(JSON.parse(raw) as Partial<T>) } : initial;
    } catch {
      return initial;
    }
  });

  const { data: savedSettings, isSuccess: loaded } = useQuery({
    queryKey,
    enabled: !!businessId,
    queryFn: async (): Promise<T | null> => {
      const { data, error } = await (supabase as any)
        .from("workflow_message_settings")
        .select("content_mode, ai_source_ids, template_source_ids, ai_instructions, config")
        .eq("business_id", businessId)
        .eq("workflow_id", workflowId)
        .maybeSingle();
      if (error) throw error;
      return data ? fromServer(data as WorkflowMessageSettingsRow, initial) : null;
    },
  });

  const persistToServer = async (next: T) => {
    if (!businessId) return;
    queryClient.setQueryData(queryKey, next);
    const payload = toServer(next);
    const { error } = await (supabase as any)
      .from("workflow_message_settings")
      .upsert(
        {
          business_id: businessId,
          workflow_id: workflowId,
          ...payload,
          updated_at: new Date().toISOString(),
          updated_by: user?.id ?? null,
        },
        { onConflict: "business_id,workflow_id" },
      );
    if (error) {
      toast.error("Couldn't save that setting. Please try again.");
      queryClient.invalidateQueries({ queryKey });
    }
  };

  // One-time migration from browser localStorage to `workflow_message_settings`.
  useEffect(() => {
    if (!businessId || !loaded || savedSettings) return;
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(storageKey);
    } catch {
      return;
    }
    if (!raw) return;
    try {
      const parsed = { ...initial, ...(JSON.parse(raw) as Partial<T>) };
      if (JSON.stringify(parsed) === JSON.stringify(initial)) return;
      void persistToServer(parsed).then(() => {
        try {
          window.localStorage.removeItem(storageKey);
        } catch {
          // Storage can be unavailable; server copy is authoritative.
        }
      });
    } catch {
      // Ignore malformed localStorage payload.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, loaded, savedSettings, storageKey]);

  const currentValue = businessId && savedSettings ? savedSettings : localValue;

  const update = (patch: Partial<T>) => {
    const next = { ...currentValue, ...patch };
    setLocalValue(next);
    if (!businessId) {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Ignore storage failures.
      }
      return;
    }
    void persistToServer(next);
  };

  return [currentValue, update] as const;
}

export const InfoTip = ({ label, children }: { label: string; children: string }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-xs">{children}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const HeadingWithTip = ({ id, title, tip }: { id: string; title: string; tip: string }) => (
  <div className="flex items-center gap-1.5">
    <h3 id={id} className="text-sm font-semibold text-foreground">{title}</h3>
    <InfoTip label={`About ${title.toLocaleLowerCase()}`}>{tip}</InfoTip>
  </div>
);

interface NumberFieldProps {
  id: string;
  labelledBy: string;
  value: number;
  min: number;
  max: number;
  prefix?: string;
  suffix: ReactNode;
  onCommit: (value: number) => void;
}

/** A small centred number box with text around it, e.g. "Every [10] classes". */
export const NumberField = ({ id, labelledBy, value, min, max, prefix, suffix, onCommit }: NumberFieldProps) => {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    const parsed = Math.round(Number(draft ?? value));
    setDraft(null);
    if (!Number.isFinite(parsed)) return;
    onCommit(Math.min(max, Math.max(min, parsed)));
  };
  return (
    <div className="flex items-center gap-2">
      {prefix && <span className="text-xs text-muted-foreground">{prefix}</span>}
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft ?? String(value)}
        aria-labelledby={labelledBy}
        className="h-8 w-20 text-center text-xs [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
      />
      <span className="text-xs text-muted-foreground">{suffix}</span>
    </div>
  );
};

interface CheckRowProps {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}

export const CheckRow = ({ id, label, hint, checked, disabled = false, onChange }: CheckRowProps) => (
  <div className="flex items-center gap-3">
    <Checkbox
      id={id}
      checked={checked}
      disabled={disabled}
      className="border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background"
      onCheckedChange={(value) => onChange?.(value === true)}
    />
    <label htmlFor={id} className={`text-xs font-medium text-foreground ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
      {label}
    </label>
    <InfoTip label={`About ${label.toLocaleLowerCase()}`}>{hint}</InfoTip>
  </div>
);

export interface MessageContentValue {
  contentMode: "ai" | "template";
  instructions: string;
  sourceId: string | null;
}

interface MessageContentFieldsProps {
  id: string;
  /** What the message is called in the descriptions, e.g. "follow-up". */
  noun: string;
  tip: string;
  instructionsPlaceholder: string;
  value: MessageContentValue;
  sources: WaveSource[];
  sourcesLoading: boolean;
  onChange: (patch: Partial<MessageContentValue>) => void;
}

export const MessageContentFields = ({
  id,
  noun,
  tip,
  instructionsPlaceholder,
  value,
  sources,
  sourcesLoading,
  onChange,
}: MessageContentFieldsProps) => (
  <div>
    <HeadingWithTip id={`${id}-content-heading`} title="Message content" tip={tip} />
    <RadioGroup
      className="mt-4 gap-2"
      value={value.contentMode}
      aria-labelledby={`${id}-content-heading`}
      onValueChange={(mode) => onChange({
        contentMode: mode as MessageContentValue["contentMode"],
        // Only templated messages use a source.
        ...(mode === "ai" ? { sourceId: null } : {}),
      })}
    >
      <label
        className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
          value.contentMode === "ai" ? "border-foreground/35 bg-muted/60" : "border-border hover:bg-muted/40"
        }`}
      >
        <RadioGroupItem value="ai" aria-label="AI-generated" className="mt-0.5 border-foreground text-foreground" />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            AI-generated
          </span>
          <span className="mt-1 block text-xs leading-4 text-muted-foreground">
            Dibs writes each {noun} using customer and knowledge context.
          </span>
        </span>
      </label>
      <label
        className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
          value.contentMode === "template" ? "border-foreground/35 bg-muted/60" : "border-border hover:bg-muted/40"
        }`}
      >
        <RadioGroupItem value="template" aria-label="Templated" className="mt-0.5 border-foreground text-foreground" />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <FileText className="h-3.5 w-3.5" />
            Templated
          </span>
          <span className="mt-1 block text-xs leading-4 text-muted-foreground">
            Send the same pre-written {noun} to every customer.
          </span>
        </span>
      </label>
    </RadioGroup>

    {value.contentMode === "ai" ? (
      <div className="mt-4">
        <label htmlFor={`${id}-instructions`} className="text-xs font-medium text-foreground">
          Instructions for message generated
        </label>
        <Textarea
          id={`${id}-instructions`}
          value={value.instructions}
          maxLength={MAX_INSTRUCTIONS_LENGTH}
          placeholder={instructionsPlaceholder}
          className="mt-1.5 min-h-[96px] text-xs"
          onChange={(event) => onChange({ instructions: event.target.value })}
        />
      </div>
    ) : (
      <div className="mt-4">
        <WaveSourcePicker
          waveId={id}
          label="Source"
          hint={`Knowledge used for templated ${noun}s.`}
          sources={sources}
          loading={sourcesLoading}
          selectedId={value.sourceId}
          disabled={false}
          onChange={(sourceId) => onChange({ sourceId })}
        />
      </div>
    )}
  </div>
);
